import React from 'react';

import castArray from 'lodash/castArray';

import { padding } from '@lumapps/classnames';
import {
    MetadataPickerField,
    MetadataPickerFieldProps,
} from '@lumapps/metadata-pickers/components/MetadataPickerField';
import type { PickerMetaData } from '@lumapps/metadata-pickers/types';
import type { Metadata } from '@lumapps/metadata/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useFormField } from '../../hooks/useFormField';
import type { BaseInput } from '../../types';

export interface FormMetadataFieldProps
    extends Omit<MetadataPickerFieldProps, 'selectionType' | 'onChange' | 'onBlur' | 'value' | 'familyId'> {
    selectionType?: MetadataPickerFieldProps['selectionType'];
    family: Metadata;
}

/**
 * Metadata field in order to use in a context of a Form
 * @param FormMetadataFieldProps
 * @returns FormMetadataField
 */
export const FormMetadataField: React.FC<FormMetadataFieldProps & Omit<BaseInput, 'label'>> = ({
    controllerProps,
    family,
    name,
    showMultipleLevels,
    selectionType: selectionTypeFromProps,
    ...props
}) => {
    const { translateObject, translateKey } = useTranslate();
    const label = translateObject(family.name) || `metadatas.${family.id}`;
    const {
        field,
        valueToUse = [],
        fieldState,
    } = useFormField<Metadata[] | Metadata | undefined>({
        name,
        controllerProps,
        label,
    });

    const selectionType = selectionTypeFromProps || (family.multiple ? 'multiple' : 'single');
    const selectionProps = React.useMemo(() => {
        if (selectionType === 'multiple') {
            return {
                selectionType: 'multiple',
                value: castArray(valueToUse),
            } as const;
        }
        return {
            selectionType: 'single',
            value: Array.isArray(valueToUse) ? valueToUse[0] : valueToUse,
        } as const;
    }, [selectionType, valueToUse]);

    const errorMessage = fieldState.error?.message || translateKey(GLOBAL.FIELD_IS_REQUIRED);

    const onChange = React.useCallback(
        (newValue: PickerMetaData | PickerMetaData[] | undefined) => {
            field.onChange(
                // Using `null` to clear the value (as `undefined` does not work)
                newValue || null,
            );
        },
        [field],
    );
    return (
        <MetadataPickerField
            {...props}
            label={label}
            familyId={family.id}
            onBlur={field.onBlur}
            instanceId={family.instance}
            showMultipleLevels={showMultipleLevels}
            // fields displayed inside a form should have no padding since distances between
            // fields are managed by the fieldset they are displayed in. This removes the
            // default padding for the Select
            className={padding('vertical', null)}
            onChange={onChange}
            {...selectionProps}
            error={fieldState.error ? errorMessage : undefined}
        />
    );
};
